/**
 * SupportPage — list page at `/support`.
 *
 * Web port of Flutter's `SupportScreen`
 * (`lib/presentation/web/support_screen.dart:36-280`). Renders the centered
 * "Support" title, the 19 category cards (one per non-`supportRequest`
 * `SupportCategoryItem`), and an embedded submit-request form below.
 * Tapping a card navigates to `/support/{id}`. Tapping Submit drives the
 * same validate → confirm → POST → result flow as the Contact page, with a
 * full-page loading overlay during the request.
 *
 * Structural template: `ContactPage.tsx` — same shared `ExpauseAppBar`
 * (`presentation/components/common/appBar/ExpauseAppBar.tsx`), same
 * `useBasePage` + `screenKey` + `useKeepAlive` wiring, same centered-column
 * body, same loading-overlay pattern.
 *
 * App-bar provenance: the ported `lib/presentation/web/support_screen.dart`
 * has **no** `Scaffold.appBar` anywhere — it renders "Support" as an in-body
 * heading at the `index == 0` slot of the category `ListView`
 * (`support_screen.dart:58-66`) — so the bar is a pre-existing web-only
 * addition (`workflow/lessons.md` — a full-page web route needs a back
 * affordance or a deep-linked user is stranded). Its shape follows the
 * *mobile* WebView screen `lib/presentation/screens/legal/support_screen.dart:106-123`
 * (back leading + centered title), a different screen from the ported one.
 * This is not a Flutter-parity claim for the bar itself.
 *
 * Moving from the hand-rolled bar to `ExpauseAppBar` makes three intentional
 * visual changes: the title steps from `responsive.font.lg`/`semibold` to
 * `responsive.font.xl`/`bold`, the bar becomes sticky with a blurred
 * 50%-opacity backdrop, and the top safe-area inset is now honoured.
 *
 * Reuses:
 * - `useSupportItems` — 20-item static catalog (Task 1).
 * - `useContact` — content/email state + validate→confirm→submit
 *   orchestration. Flutter implements the same orchestration inline in the
 *   support screen (`support_screen.dart:177-248`); the web factors it into
 *   the shared hook because both Contact and Support call the identical
 *   `submitSupportRequest` cloud function.
 * - `ContactRequestForm` — the content + email + Submit inputs. The only
 *   divergence between the two pages' forms is the leading label, so the
 *   component takes an optional `labelText` prop (passed here as
 *   `l10n.support.requestSectionLabel`) rather than being duplicated.
 *
 * **Web deviations from Flutter** (Flutter parity scope is business logic;
 * UI follows web norms per `conventions.md` / `presentation.md`):
 *
 * - Flutter uses a single `ListView.builder` over `items.length + 1` and
 *   special-cases the `supportRequest` item to render as the submit form
 *   inline (`support_screen.dart:69-73`). The web filters that item out of
 *   the card list and appends `ContactRequestForm` below — same observable
 *   result, simpler render tree, and lets the form sit naturally below the
 *   list without fighting a list-shell. The plan explicitly authorises this
 *   in Task 3's "VirtualList rule" note.
 * - Flutter card uses hardcoded `width: 400` + a custom `BoxShadow`; the
 *   web uses `responsive.dialog.maxWidth` for the clamp and `theme.shadow`
 *   for the elevation tint (no inline hex / hardcoded pixels per
 *   `presentation.md`).
 * - Flutter shows `InfoDialog` / `ConfirmDialog`; the web shows `InfoSheet`
 *   / `ConfirmSheet` (project convention — sheets, not dialogs).
 *
 * Accessible without authentication; mounted under `PUBLIC_ROUTES`.
 */
import { useCallback, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { Navigator } from '@/presentation/routing/Navigator';
import { ExpauseAppBar } from '@/presentation/components/common/appBar/ExpauseAppBar';
import { ExpauseLoadingIndicator } from '@/presentation/components/common/loadingIndicators/ExpauseLoadingIndicator';
import { useBasePage } from '@/presentation/hooks/useBasePage';
import { useKeepAlive } from '@/presentation/routing/RouteStack';
import { generateScreenKey } from '@/presentation/routing/screenKeyUtils';
import { SupportCategory } from '@/domain/enums/support/supportCategory';
import { useContact } from '@/presentation/feature/contact/hooks/useContact';
import { useContactSheetSurface } from '@/presentation/feature/contact/hooks/useContactSheetSurface';
import { ContactRequestForm } from '@/presentation/feature/contact/components/ContactRequestForm';
import { Divider } from '@/presentation/components/common/Divider';
import { useSupportItems } from './hooks/useSupportItems';
import { SupportCategoryCard } from './components/SupportCategoryCard';
import { qaAttr } from '@/presentation/utils/qaAttr';

export const SupportPage = () => {
    const { theme } = useTheme();
    const responsive = useResponsiveDesign();
    const { l10n } = useLocale();
    const location = useLocation();

    // Canonical screen-key wiring (mirrors `ContactPage` / `LegalDocumentsPage`).
    const { currentScreenKeyRef } = useKeepAlive();
    const screenKeyRef = useRef<string>(
        currentScreenKeyRef.current || generateScreenKey(location.pathname),
    );
    const screenKey = screenKeyRef.current;

    useBasePage({ screenKey, onInit: () => { } });

    const items = useSupportItems();

    // Flutter's data has exactly one `supportRequest` entry at array index 19
    // (`support_utils.dart:103-107`); the list page filters it out and renders
    // the embedded submit form below. Invariant: `items` always has 20 entries
    // with the last being `supportRequest`.
    const cardItems = useMemo(
        () => items.filter((item) => item.category !== SupportCategory.supportRequest),
        [items],
    );

    // useContact surface wiring — shared with ContactPage via
    // `useContactSheetSurface`. Flutter inlines the exact same English
    // strings on both screens (`support_screen.dart:181-247` vs
    // `contact_screen.dart`), so a single hook owns the wiring for both.
    const surface = useContactSheetSurface(screenKey);
    const { content, email, isLoading, setContent, setEmail, submit } = useContact(surface);

    const handleCardClick = useCallback((id: number) => {
        Navigator.navigateToSupportItem(id);
    }, []);

    // Deep-link guard: with no in-app history, fall back
    // to the feed (same pattern as ContactPage / LegalDocumentsPage).
    const handleBack = () => Navigator.goBackOr(() => Navigator.navigateToFeed());

    return (
        <div
            {...qaAttr({ id: 'support', status: isLoading ? 'submitting' : 'ready' })}
            style={{
                position: 'relative',
                minHeight: '100vh',
                backgroundColor: theme.background,
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            {/* App bar — back button + centered title (mirrors ContactPage). */}
            <ExpauseAppBar
                rootQa={{ id: 'support-app-bar' }}
                title={l10n.support.title}
                leadingQa={{ id: 'support-back' }}
                onLeadingTap={handleBack}
            />

            {/* Body — centered column with cards + form. */}
            <div
                style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: `${responsive.spacing.lg}px`,
                }}
            >
                <div
                    style={{
                        width: '100%',
                        maxWidth: responsive.dialog.maxWidth,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: responsive.spacing.lg,
                    }}
                >
                    {cardItems.map((item) => (
                        <SupportCategoryCard
                            key={item.id}
                            item={item}
                            onClick={handleCardClick}
                        />
                    ))}

                    <Divider margin={responsive.spacing.lg} />

                    <ContactRequestForm
                        content={content}
                        email={email}
                        setContent={setContent}
                        setEmail={setEmail}
                        onSubmit={submit}
                        labelText={l10n.support.requestSectionLabel}
                        qaIdPrefix="support-request"
                    />
                </div>
            </div>

            {/* Loading overlay (mirrors ContactPage's overlay).
                `position: 'fixed'` anchors both the dim layer *and* the
                centered spinner to the viewport rather than the
                document-relative parent — without this, when the user
                scrolls down to the submit button the spinner stays
                centered on the (taller-than-viewport) document and drifts
                off-screen, leaving only a dimmed page with no visible
                activity indicator. `zIndex: 9999` matches the project's
                existing full-viewport overlay convention
                (`VideoUploadProgressOverlay`, `EditUserProfilePage`) and
                sits well above sheet z-indices (sheets start at 100). */}
            {isLoading && (
                <div
                    style={{
                        position: 'fixed',
                        inset: 0,
                        backgroundColor: theme.modalOverlay,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 9999,
                    }}
                >
                    <ExpauseLoadingIndicator />
                </div>
            )}
        </div>
    );
};
